import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaFilter, FaHistory, FaCalendar, FaChevronDown, FaCheck, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { campaignService } from '../../services/companyAuthService';

const PaymentRecords = () => {
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    campaigns: []
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  useEffect(() => {
    fetchPaymentRecords();
  }, []);

  const fetchPaymentRecords = async () => {
    try {
      setLoading(true);
      const response = await campaignService.getAllPaymentRecords();
      
      if (response.success) {
        setPaymentRecords(response.data.paymentRecords || []);
        setFilterOptions(response.data.filterOptions || {
          brands: [],
          campaigns: []
        });
      }
    } catch (error) {
      console.error('Error fetching payment records:', error);
      toast.error('Failed to load payment records');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredRecords = useMemo(() => {
    let filtered = [...paymentRecords];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(record =>
        record.brandName?.toLowerCase().includes(search) ||
        record.campaignName?.toLowerCase().includes(search) ||
        record.influencerName?.toLowerCase().includes(search) ||
        record.influencerEmail?.toLowerCase().includes(search) ||
        record.referenceMessage?.toLowerCase().includes(search)
      );
    }

    // Brand filter
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(record => record.brandName === selectedBrand);
    }

    // Campaign filter
    if (selectedCampaign !== 'all') {
      filtered = filtered.filter(record => record.campaignName === selectedCampaign);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(record => record.status === selectedStatus);
    }

    // Date filter
    if (dateFilter.startDate) {
      filtered = filtered.filter(record =>
        new Date(record.date) >= new Date(dateFilter.startDate)
      );
    }
    if (dateFilter.endDate) {
      filtered = filtered.filter(record =>
        new Date(record.date) <= new Date(dateFilter.endDate)
      );
    }

    return filtered;
  }, [paymentRecords, searchTerm, selectedBrand, selectedCampaign, selectedStatus, dateFilter]);

  // Calculate filtered summary
  const filteredSummary = useMemo(() => {
    return filteredRecords.reduce((acc, record) => {
      acc.totalEarnings += record.totalEarnings;
      acc.totalPaid += record.paidAmount;
      acc.totalPending += record.pendingAmount;
      acc.totalTransactions += 1;
      return acc;
    }, {
      totalEarnings: 0,
      totalPaid: 0,
      totalPending: 0,
      totalTransactions: 0
    });
  }, [filteredRecords]);

  // Pagination
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  }, [filteredRecords, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedCampaign('all');
    setSelectedStatus('all');
    setDateFilter({ startDate: '', endDate: '' });
    setCurrentPage(1);
  };



  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'Fully Paid': 'bg-green-100 text-green-800',
      'Partial Payment': 'bg-yellow-100 text-yellow-800',
      'Unpaid': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-full">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
              <FaHistory className="text-orange-500" />
              Payment Records
            </h1>
            <p className="text-sm text-gray-600 mt-1">Complete transaction history and payment statements</p>
          </div>
          <div className="flex gap-2">
            {/* <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm"
            >
              <FaDownload />
              Export CSV
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm print:hidden"
            >
              <FaPrint />
              Print
            </button> */}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 print:mb-4">
        <div className="bg-white border-gray-200 rounded-xl shadow-lg p-4 text-green-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Earnings</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(filteredSummary.totalEarnings)}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-white border-gray-200 rounded-xl shadow-lg p-4 text-orange-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Paid</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(filteredSummary.totalPaid)}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white border-gray-200 rounded-xl shadow-lg p-4 text-red-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Pending</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(filteredSummary.totalPending)}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <span className="text-2xl">⏳</span>
            </div>
          </div>
        </div>

        <div className="bg-white border-gray-200 rounded-xl shadow-lg p-4 text-gray-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Total Transactions</p>
              <p className="text-2xl font-bold mt-1">{filteredSummary.totalTransactions}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <span className="text-2xl">📊</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 print:hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <FaFilter className="text-gray-600" />
            <span className="font-semibold text-gray-800">Filters</span>
            {(searchTerm || selectedBrand !== 'all' || selectedCampaign !== 'all' || selectedStatus !== 'all' || dateFilter.startDate || dateFilter.endDate) && (
              <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">Active</span>
            )}
          </div>
          <FaChevronDown className={`text-gray-600 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </div>

        {showFilters && (
          <div className="p-4 border-t border-gray-200 space-y-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaSearch className="inline mr-2" />
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by brand, campaign, influencer, email, or reference..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Brand Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">All Brands</option>
                  {filterOptions.brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              {/* Campaign Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
                <select
                  value={selectedCampaign}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">All Campaigns</option>
                  {filterOptions.campaigns.map(campaign => (
                    <option key={campaign} value={campaign}>{campaign}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">All Status</option>
                  <option value="Fully Paid">Fully Paid</option>
                  <option value="Partial Payment">Partial Payment</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaCalendar className="inline mr-2" />
                  Date Range
                </label>
                <div className="space-y-2">
                  <input
                    type="date"
                    value={dateFilter.startDate}
                    onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={dateFilter.endDate}
                    onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                    placeholder="End Date"
                  />
                </div>
              </div>
            </div>

            {/* Reset Filters Button */}
            <div className="flex justify-end">
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Records Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Influencer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Earnings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmed</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecords.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <div className="text-4xl mb-2">📋</div>
                      <p className="text-lg font-medium text-gray-900 mb-1">No payment records found</p>
                      <p className="text-sm text-gray-500">Try adjusting your filters or search criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedRecords.map((record, index) => (
                  <tr key={`${record._id}-${index}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {new Date(record.date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {record.brandName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {record.campaignName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>
                        <div className="font-medium">{record.influencerName}</div>
                        <div className="text-xs text-gray-500">{record.influencerEmail}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(record.totalEarnings)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600">
                      {formatCurrency(record.paidAmount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-orange-600">
                      {formatCurrency(record.pendingAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                      <div className="truncate" title={record.referenceMessage}>
                        {record.referenceMessage || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {getStatusBadge(record.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      {record.paidAmount > 0 ? (
                        record.isReceived ? (
                          <span className="flex items-center justify-center text-green-600 font-semibold">
                            <FaCheck className="mr-1" />
                            Yes
                          </span>
                        ) : (
                          <span className="flex items-center justify-center text-red-600">
                            <FaTimes className="mr-1" />
                            No
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6 print:hidden">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredRecords.length)}</span> of{' '}
                <span className="font-medium">{filteredRecords.length}</span> results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex gap-1">
                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 border rounded-md text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-orange-500 text-white border-orange-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          .container {
            max-width: 100%;
            padding: 0;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentRecords;
